import axios from 'axios';
import format from 'date-fns/format';
import { stringify } from 'query-string';

import {
  openSuccessPopup,
  openErrorPopup,
} from 'containers/App/actions';
import { decryptText } from 'utils/encryption';
import { formatDateString } from 'utils/formatString';

import {
  API_DATE_FORMAT,
  FETCH_EXPENSES,
  FETCH_EXPENSES_SUCCESS,
  FETCH_EXPENSES_ERROR,
  FETCH_EXPENSES_SERVER_ERROR,
  FETCH_PAYMENTS,
  FETCH_PAYMENTS_SUCCESS,
  FETCH_PAYMENTS_ERROR,
  UPDATE_PERIOD_START,
  UPDATE_PERIOD_END,
  FETCH_EMAIL,
  FETCH_EMAIL_ERROR,
  FETCH_EMAIL_SUCCESS,
  TOGGLE_EXPENSES_FILTER,
  TOGGLE_ONLY_PAID_FILTER,
  FETCH_POSTPAID_INVOICES,
  FETCH_POSTPAID_INVOICES_SUCCESS,
  FETCH_POSTPAID_INVOICES_ERROR,
  SEND_INVOICE_TO_EMAIL,
  SEND_INVOICE_TO_EMAIL_SUCCESS,
  SEND_INVOICE_TO_EMAIL_ERROR,
  FETCH_INVOICE_DETAILS,
  FETCH_INVOICE_DETAILS_SUCCESS,
  FETCH_INVOICE_DETAILS_ERROR,
  CHECK_EMAIL_NOTIFICATIONS,
  CHECK_EMAIL_NOTIFICATIONS_SUCCESS,
  CHECK_EMAIL_NOTIFICATIONS_ERROR,
  SAVE_DETALIZATION,
  SAVE_DETALIZATION_SUCCESS,
  SAVE_DETALIZATION_ERROR,
  TOGGLE_PAYMENTS_DETAILS_ROW,
  FETCH_INTERNET_STATS,
  FETCH_INTERNET_STATS_ERROR,
  FETCH_INTERNET_STATS_SUCCESS,
  FETCH_CASHFLOW_STATS,
  FETCH_CASHFLOW_STATS_ERROR,
  FETCH_CASHFLOW_STATS_SUCCESS,
  UPDATE_INTERNET_PERIOD_START,
  UPDATE_INTERNET_PERIOD_END,
} from './constants';

let isDownloadingStopped = false;

const formatDate = (date) => format(date, API_DATE_FORMAT);

const getYesterday = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date;
};

const dataLayerPush = {
  downloadReport: () => {
    window.dataLayer.push({
      event: 'LK_event',
      LKaction: 'Report Saving',
    });
  },
};

const wait = (delay) => new Promise((resolve) => setTimeout(() => resolve(), delay));

const downloadFileByUrl = (url) => {
  const link = document.createElement('a');

  link.href = url;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

async function downloadFile(responseData) {
  const {
    requestId,
    pollingInterval,
    pollingTimeout,
  } = responseData;

  if (requestId === -1) {
    throw responseData || new Error('Downloading error');
  }

  isDownloadingStopped = false;

  const file = `/api/profile/detalization/detalizationreport/?${stringify({
    requestId,
    unZip: false,
  })}`;

  let counter = 0;

  while (counter < pollingTimeout / pollingInterval) {
    await wait(pollingInterval * 1000);

    const { data: success } = await axios.get('/api/profile/detalization/requeststatus/', { params: { requestId } });

    if (isDownloadingStopped) {
      return true;
    }

    if (success) {
      downloadFileByUrl(file);

      return success;
    }

    counter += 1;
  }

  throw new Error('Polling timeout');
}

export function fetchExpensesStart() {
  return {
    type: FETCH_EXPENSES,
  };
}

export function fetchExpensesSuccess(data) {
  return {
    type: FETCH_EXPENSES_SUCCESS,
    data,
  };
}

export function fetchExpensesError(error, isServerError = false) {
  if (isServerError) {
    return {
      type: FETCH_EXPENSES_SERVER_ERROR,
      error: 'SERVER_ERROR',
    };
  }

  return {
    type: FETCH_EXPENSES_ERROR,
    error,
  };
}

export function fetchPostpaidInvoiceDetailsStart() {
  return {
    type: FETCH_INVOICE_DETAILS,
  };
}

export function fetchPostpaidInvoiceDetailsSuccess(data) {
  return {
    type: FETCH_INVOICE_DETAILS_SUCCESS,
    data,
  };
}

export function fetchPostpaidInvoiceDetailsError(error) {
  return {
    type: FETCH_INVOICE_DETAILS_ERROR,
    error,
  };
}

export function fetchExpenses() {
  return (dispatch, getState) => {
    dispatch(fetchExpensesStart());

    const {
      detalization: { period },
      application: { isPorted },
    } = getState();

    axios.get(`/api/profile/detalization/expensestructure${isPorted ? 'ported' : ''}/`, {
      params: {
        periodStart: formatDate(period.start),
        periodEnd: formatDate(period.end),
      },
    })
      .then(({ data }) => {
        if (data.tooManyTransactions) {
          dispatch(fetchExpensesError('TOO_MANY_TRANSACTIONS'));
          return;
        }

        if (data.error) {
          dispatch(fetchExpensesError(data.error));
        } else {
          dispatch(fetchExpensesSuccess({ ...data, period }));
        }
      })
      .catch((error) => dispatch(fetchExpensesError(error, true)));
  };
}

export function fetchPaymentsStart() {
  return {
    type: FETCH_PAYMENTS,
  };
}

export function fetchPaymentsSuccess(data) {
  return {
    type: FETCH_PAYMENTS_SUCCESS,
    data,
  };
}

export function fetchPaymentsError(error) {
  return {
    type: FETCH_PAYMENTS_ERROR,
    error,
  };
}

export function fetchPayments() {
  return (dispatch, getState) => {
    dispatch(fetchPaymentsStart());

    const { application: { isPostpaid } } = getState();
    const apiURI = isPostpaid
      ? '/api/profile/detalization/paymentspostpaid/'
      : '/api/profile/detalization/payments/';

    axios.get(apiURI)
      .then((response) => dispatch(fetchPaymentsSuccess(response.data)))
      .catch((error) => dispatch(fetchPaymentsError(error.response)));
  };
}

export function downloadReport(reportFormat, isEmailEnabled) {
  return async (dispatch, getState) => {
    const { detalization: { period } } = getState();

    dataLayerPush.downloadReport();

    const { data } = await axios.post('/api/profile/detalization/detalizationrequest/', {
      periodStart: formatDate(period.start),
      periodEnd: formatDate(period.end),
      reportType: reportFormat,
      isEmailSend: isEmailEnabled,
    });

    return downloadFile(data);
  };
}

export function updatePeriodStart(date) {
  return {
    type: UPDATE_PERIOD_START,
    date,
  };
}

export function updatePeriodEnd(date) {
  return {
    type: UPDATE_PERIOD_END,
    date,
  };
}

export function updateInternetPeriodStart(date) {
  return {
    type: UPDATE_INTERNET_PERIOD_START,
    date: date || getYesterday(),
  };
}

export function updateInternetPeriodEnd(date) {
  return {
    type: UPDATE_INTERNET_PERIOD_END,
    date: date || new Date(),
  };
}

export function fetchEmail() {
  return { type: FETCH_EMAIL };
}

export function fetchEmailSuccess(emailInfo) {
  return { type: FETCH_EMAIL_SUCCESS, emailInfo };
}

export function fetchEmailError(error) {
  return { type: FETCH_EMAIL_ERROR, error };
}

export function fetchIsEmailExist() {
  return (dispatch) => {
    dispatch(fetchEmail());

    return axios.get('/api/profile/detalization/checkemailnotifications/')
      .then((response) => dispatch(fetchEmailSuccess(response.data)))
      .catch((error) => dispatch(fetchEmailError(error.response)));
  };
}

export function fetchInternetStatsStart() {
  return {
    type: FETCH_INTERNET_STATS,
  };
}

export function fetchInternetStatsSuccess(data) {
  return {
    data,
    type: FETCH_INTERNET_STATS_SUCCESS,
  };
}

export function fetchInternetStatsError(error) {
  return {
    error,
    type: FETCH_INTERNET_STATS_ERROR,
  };
}

export function fetchInternetStats(dateFrom, dateTo) {
  return async (dispatch) => {
    dispatch(fetchInternetStatsStart());

    try {
      let config = null;
      if (dateFrom && dateTo) {
        config = { params: { dateFrom, dateTo } };
      }
      const { data } = await axios.get('/api/profile/detalization/internetstats/', config);

      const transformedData = {
        ...data,
        internetStatsRows: data.internetStatsRows
          && data.internetStatsRows.reduce((res, sessionItem) => {
            let month = formatDateString(sessionItem.startSessionTime, 'MMMM YYYY');
            month = month.charAt(0).toUpperCase() + month.slice(1);
            const monthRow = res.find((row) => row.month === month);
            if (monthRow) {
              monthRow.sessionsData.push(sessionItem);
              monthRow.receivedData += sessionItem.receivedData;
              monthRow.transferredData += sessionItem.transferredData;
              monthRow.cost += sessionItem.cost;
              monthRow.sessionDuration += sessionItem.sessionDuration;
            } else {
              res.push({ month, sessionsData: [sessionItem], ...sessionItem });
            }
            return res;
          }, []),
      };

      dispatch(fetchInternetStatsSuccess(transformedData));
    } catch (error) {
      dispatch(fetchInternetStatsError(error.message));
    }
  };
}

export function fetchCashflowStatsStart() {
  return {
    type: FETCH_CASHFLOW_STATS,
  };
}

export function fetchCashflowStatsSuccess(data) {
  return {
    data,
    type: FETCH_CASHFLOW_STATS_SUCCESS,
  };
}

export function fetchCashflowStatsError(error) {
  return {
    error,
    type: FETCH_CASHFLOW_STATS_ERROR,
  };
}

export function fetchCashflowStats(dateFrom, dateTo) {
  return async (dispatch) => {
    dispatch(fetchCashflowStatsStart());

    try {
      let config = null;
      if (dateFrom && dateTo) {
        config = { params: { dateFrom, dateTo } };
      }
      const { data } = await axios.get('/api/profile/detalization/cashflowstats/', config);

      dispatch(fetchCashflowStatsSuccess(data));
    } catch (error) {
      dispatch(fetchCashflowStatsError(error.message));
    }
  };
}

export function toggleExpensesFilter(data) {
  return {
    data,
    type: TOGGLE_EXPENSES_FILTER,
  };
}

export function toggleOnlyPaidFilter() {
  return {
    type: TOGGLE_ONLY_PAID_FILTER,
  };
}

export function fetchPostpaidInvoicesStart() {
  return {
    type: FETCH_POSTPAID_INVOICES,
  };
}

export function fetchPostpaidInvoicesSuccess(data) {
  return {
    data,
    type: FETCH_POSTPAID_INVOICES_SUCCESS,
  };
}

export function fetchPostpaidInvoicesError(error) {
  return {
    error,
    type: FETCH_POSTPAID_INVOICES_ERROR,
  };
}

export function fetchPostpaidInvoices() {
  return async (dispatch) => {
    dispatch(fetchPostpaidInvoicesStart());

    try {
      const { data } = await axios.get('/api/profile/detalization/invoices/');

      dispatch(fetchPostpaidInvoicesSuccess(data));
    } catch (error) {
      dispatch(fetchPostpaidInvoicesError(error.message));
    }
  };
}

export function fetchPostpaidExpenses(params) {
  return async (dispatch) => {
    dispatch(fetchExpensesStart());

    try {
      const { data } = await axios.get('/api/profile/detalization/expensestructurepostpaid/', {
        params: {
          ...params,
          ctnFor: decryptText(params.ctnFor),
        },
      });

      dispatch(fetchExpensesSuccess({
        ...data,
        ctnFor: decryptText(params.ctnFor),
      }));
    } catch (error) {
      dispatch(fetchExpensesError(error.message));
    }
  };
}

export function fetchPostpaidInvoiceDetails(params) {
  return async (dispatch) => {
    dispatch(fetchPostpaidInvoiceDetailsStart());

    try {
      const { data } = await axios.get('/api/profile/detalization/postpaidinvoicedetails/', { params });

      dispatch(fetchPostpaidInvoiceDetailsSuccess(data));
    } catch (error) {
      dispatch(fetchPostpaidInvoiceDetailsError(error.message));
    }
  };
}

export function sendInvoiceToEmailStart() {
  return {
    type: SEND_INVOICE_TO_EMAIL,
  };
}

export function sendInvoiceToEmailSuccess(email) {
  return {
    data: email,
    type: SEND_INVOICE_TO_EMAIL_SUCCESS,
  };
}

export function sendInvoiceToEmailError(error) {
  return {
    error,
    type: SEND_INVOICE_TO_EMAIL_ERROR,
  };
}

export function sendInvoiceToEmail({
  email,
  invoiceNumber,
  benNumber,
}) {
  return async (dispatch) => {
    dispatch(sendInvoiceToEmailStart());

    try {
      await axios.post('/api/profile/detalization/sendinvoicetoemail/', {
        email,
        invoiceNumber,
      });

      dispatch(sendInvoiceToEmailSuccess({
        email,
        benNumber,
      }));
    } catch (error) {
      dispatch(sendInvoiceToEmailError(error.message));
    }
  };
}

export function checkEmailNotificationsStart() {
  return {
    type: CHECK_EMAIL_NOTIFICATIONS,
  };
}

export function checkEmailNotificationsSuccess(isEmailConfirmed) {
  return {
    data: isEmailConfirmed,
    type: CHECK_EMAIL_NOTIFICATIONS_SUCCESS,
  };
}

export function checkEmailNotificationsError(error) {
  return {
    error,
    type: CHECK_EMAIL_NOTIFICATIONS_ERROR,
  };
}

export function checkEmailNotifications() {
  return async (dispatch) => {
    dispatch(checkEmailNotificationsStart());

    try {
      const { data } = await axios.get('/api/profile/detalization/checkemailnotifications/');

      dispatch(checkEmailNotificationsSuccess(data.isEmailExist));
    } catch (error) {
      dispatch(checkEmailNotificationsError(error.message));
    }
  };
}

export function saveDetalizationStart() {
  return {
    type: SAVE_DETALIZATION,
  };
}

export function saveDetalizationSuccess(data) {
  return {
    data,
    type: SAVE_DETALIZATION_SUCCESS,
  };
}

export function saveDetalizationError(error) {
  return {
    error,
    type: SAVE_DETALIZATION_ERROR,
  };
}

export function saveDetalization(params, showStatusPopup = false) {
  return async (dispatch) => {
    dispatch(saveDetalizationStart());

    try {
      downloadFileByUrl(`/api/profile/detalization/postpaiddetail/?${stringify(params)}`);

      dispatch(saveDetalizationSuccess());

      if (showStatusPopup) {
        dispatch(openSuccessPopup({
          message: 'detalization.postpaidInvoices.invoice.saveDetalizationPopup.successTitle',
          messageAsTranslationKey: true,
        }));
      }
    } catch (error) {
      dispatch(saveDetalizationError(error.message || 'Download error'));

      if (showStatusPopup) {
        dispatch(openErrorPopup());
      }
    }
  };
}

export function saveDetalizationForCtn(params, showStatusPopup = false) {
  return async (dispatch) => {
    dispatch(saveDetalizationStart());
    dataLayerPush.downloadReport();

    try {
      const { data } = await axios.post('/api/profile/detalization/detalizationrequest/', params);

      await downloadFile(data);

      dispatch(saveDetalizationSuccess());

      if (showStatusPopup) {
        dispatch(openSuccessPopup({
          message: 'detalization.postpaidInvoices.invoice.saveDetalizationPopup.successTitle',
          messageAsTranslationKey: true,
        }));
      }
    } catch (error) {
      dispatch(saveDetalizationError(error.message || 'Download error'));

      if (showStatusPopup) {
        dispatch(openErrorPopup());
      }
    }
  };
}

export function togglePaymentsDetailsRow(data) {
  return {
    data,
    type: TOGGLE_PAYMENTS_DETAILS_ROW,
  };
}

export function fetchPostpaidInitialData() {
  return (dispatch) => {
    dispatch(fetchPostpaidInvoices());
  };
}

export function fetchInvoiceDetalizationInitialData(data) {
  return (dispatch) => {
    dispatch(fetchPostpaidInvoiceDetails(data));
    dispatch(checkEmailNotifications());
  };
}

export function fetchCtnDetalizationInitialData(data) {
  return (dispatch) => {
    dispatch(fetchPostpaidExpenses(data));
    dispatch(checkEmailNotifications());
  };
}

export function stopDownloading() {
  return (dispatch) => {
    isDownloadingStopped = true;

    dispatch(saveDetalizationSuccess());
  };
}
